import { useState, useMemo } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
  Row,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  Building2,
  UserCircle,
  Landmark,
  Heart,
  Plane,
  RotateCcw,
} from 'lucide-react'
import {
  DataTableColumnHeader,
  DataTablePagination,
} from '@/components/data-table'
import { cn } from '@/lib/utils'
import { useClientsRegistryStore } from '@/stores/clientsRegistryStore'
import type {
  ClientRegistryRow,
  ClientSavedViewId,
  ClientGroupingOption,
  ClientRelationshipFilter,
  ClientCrmStageFilter,
  ClientAnomalyFlag,
} from '@/types/clients'
import {
  CLIENT_TYPE_LABELS,
  CLIENT_CRM_STAGE_LABELS,
  CLIENT_CRM_STAGE_VARIANTS,
  CLIENT_RELATIONSHIP_LABELS,
  CLIENT_RELATIONSHIP_VARIANTS,
  CLIENT_ANOMALY_FLAGS,
  CLIENT_ALWAYS_ON_COLUMNS,
  CLIENT_VIEW_CONTEXTUAL_COLUMNS,
  getClientDisplayName,
} from '@/types/clients'
import type { ClientType } from '@/types/clients'

interface ClientsTableProps {
  data: ClientRegistryRow[]
  isLoading: boolean
  activeView: ClientSavedViewId
  onEdit: (id: string) => void
  onDelete: (id: string, name: string) => void
  onManageContacts?: (id: string) => void
  onRestore?: (id: string) => void
  onRowClick?: (id: string) => void
}

// ============================================
// Client Type Icons
// ============================================

const CLIENT_TYPE_ICONS: Record<ClientType, React.ComponentType<{ className?: string }>> = {
  corporate: Building2,
  individual: UserCircle,
  government: Landmark,
  non_profit: Heart,
}

// ============================================
// All column IDs for visibility computation
// ============================================

const ALL_COLUMN_IDS = [
  // Always-on
  'client_name', 'client_type', 'primary_contact', 'aircraft_count',
  'crm_stage', 'relationship', 'active_contracts',
  // Portfolio/All contextual
  'current_location', 'member_since',
  // Leads contextual
  'lead_source', 'last_contact', 'lead_score',
  // At Risk contextual
  'risk_flag', 'lease_expiry_days', 'nearest_lease_end',
] as const

// ============================================
// Row Grouping
// ============================================

function groupRows(
  rows: Row<ClientRegistryRow>[],
  groupBy: ClientGroupingOption
): Map<string, Row<ClientRegistryRow>[]> {
  if (groupBy === 'none') {
    return new Map([['all', rows]])
  }

  const groups = new Map<string, Row<ClientRegistryRow>[]>()

  rows.forEach((row) => {
    let groupKey: string

    switch (groupBy) {
      case 'relationship':
        groupKey = row.original.computed_relationship
        break
      case 'account_type':
        groupKey = row.original.client_type
        break
      case 'crm_stage':
        groupKey = row.original.crm_stage
        break
      default:
        groupKey = 'all'
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    groups.get(groupKey)!.push(row)
  })

  // Sort groups by predefined order
  if (groupBy === 'crm_stage') {
    const sortedGroups = new Map<string, Row<ClientRegistryRow>[]>()
    const order = ['active', 'lead', 'prospect', 'former', 'inactive']
    order.forEach((key) => { if (groups.has(key)) sortedGroups.set(key, groups.get(key)!) })
    groups.forEach((value, key) => { if (!sortedGroups.has(key)) sortedGroups.set(key, value) })
    return sortedGroups
  }

  if (groupBy === 'relationship') {
    const sortedGroups = new Map<string, Row<ClientRegistryRow>[]>()
    const order = ['based_tenant', 'transient_guest', 'both', 'no_contract']
    order.forEach((key) => { if (groups.has(key)) sortedGroups.set(key, groups.get(key)!) })
    groups.forEach((value, key) => { if (!sortedGroups.has(key)) sortedGroups.set(key, value) })
    return sortedGroups
  }

  return groups
}

function getGroupLabel(groupKey: string, groupBy: ClientGroupingOption): { label: string; emoji?: string } {
  if (groupBy === 'crm_stage') {
    const label = CLIENT_CRM_STAGE_LABELS[groupKey as ClientCrmStageFilter]
    const option = [
      { value: 'active', icon: '🟢' },
      { value: 'lead', icon: '🟡' },
      { value: 'prospect', icon: '🔵' },
      { value: 'former', icon: '⚪' },
      { value: 'inactive', icon: '🔴' },
    ].find((o) => o.value === groupKey)
    if (label) return { label, emoji: option?.icon }
  }

  if (groupBy === 'relationship') {
    const label = CLIENT_RELATIONSHIP_LABELS[groupKey as ClientRelationshipFilter]
    if (label) return { label }
  }

  if (groupBy === 'account_type') {
    const label = CLIENT_TYPE_LABELS[groupKey as ClientType]
    if (label) return { label }
  }

  return { label: groupKey }
}

// ============================================
// Coming Soon Cell
// ============================================

function ComingSoon() {
  return (
    <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground border-dashed">
      Coming Soon
    </Badge>
  )
}

// ============================================
// Lease Expiry Countdown Cell
// ============================================

function LeaseExpiryCell({ nearestEndDate }: { nearestEndDate: string | null }) {
  if (!nearestEndDate) return <span className="text-muted-foreground">—</span>

  const daysUntil = Math.ceil(
    (new Date(nearestEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  )

  if (daysUntil < 0) {
    return <span className="text-sm font-medium text-red-600 dark:text-red-400">Expired</span>
  }

  let colorClass = 'text-muted-foreground'
  if (daysUntil <= 30) colorClass = 'text-red-600 dark:text-red-400'
  else if (daysUntil <= 60) colorClass = 'text-orange-600 dark:text-orange-400'
  else if (daysUntil <= 90) colorClass = 'text-yellow-600 dark:text-yellow-400'

  return <span className={cn('text-sm font-medium', colorClass)}>{daysUntil}d</span>
}

// ============================================
// Risk Flag Cell
// ============================================

function RiskFlagCell({ anomalies, row }: { anomalies: ClientAnomalyFlag[]; row: ClientRegistryRow }) {
  if (anomalies.length === 0) return <span className="text-muted-foreground">—</span>

  const primaryFlag = CLIENT_ANOMALY_FLAGS.find(
    (f) => !f.comingSoon && anomalies.includes(f.flag)
  )
  if (!primaryFlag) return <span className="text-muted-foreground">—</span>

  let detail = ''
  if (primaryFlag.flag === 'lease_expiring' && row.nearest_lease_end_date) {
    const daysUntil = Math.ceil(
      (new Date(row.nearest_lease_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    )
    detail = `expires in ${daysUntil}d`
  } else if (primaryFlag.flag === 'repeat_visitor') {
    detail = `${row.transient_visits_12mo}x visits`
  } else if (primaryFlag.flag === 'inactive_client') {
    detail = 'inactive'
  }

  return (
    <span className={cn('inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded', primaryFlag.bgClass, primaryFlag.textClass)}>
      <span>{primaryFlag.emoji}</span>
      <span>{detail || primaryFlag.label}</span>
    </span>
  )
}

// ============================================
// Main Component
// ============================================

export function ClientsTable({
  data,
  isLoading,
  activeView,
  onEdit,
  onDelete,
  onManageContacts,
  onRestore,
  onRowClick,
}: ClientsTableProps) {
  const { groupBy, search } = useClientsRegistryStore()

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // Column visibility — pure function of activeView
  const columnVisibility = useMemo((): VisibilityState => {
    const alwaysOn = new Set<string>(CLIENT_ALWAYS_ON_COLUMNS)
    const contextual = new Set<string>(CLIENT_VIEW_CONTEXTUAL_COLUMNS[activeView])
    const vis: VisibilityState = {}
    for (const colId of ALL_COLUMN_IDS) {
      vis[colId] = alwaysOn.has(colId) || contextual.has(colId)
    }
    vis['select'] = true
    vis['actions'] = true
    return vis
  }, [activeView])

  const columns: ColumnDef<ClientRegistryRow>[] = [
    // Select
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    // === ALWAYS-ON COLUMNS (7) ===

    // 1. Client Name — clickable
    {
      id: 'client_name',
      accessorFn: (row) => getClientDisplayName(row),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Client Name" />
      ),
      cell: ({ row }) => {
        const isDeleted = !!row.original.deleted_at
        return (
          <div className="min-w-0">
            <span className={cn('font-medium truncate', isDeleted && 'line-through text-muted-foreground')}>
              {getClientDisplayName(row.original)}
            </span>
          </div>
        )
      },
      enableHiding: false,
    },

    // 2. Client Type — icon only
    {
      id: 'client_type',
      accessorFn: (row) => row.client_type,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => {
        const type = row.original.client_type
        const Icon = CLIENT_TYPE_ICONS[type]
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Icon className="h-4 w-4 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{CLIENT_TYPE_LABELS[type]}</TooltipContent>
          </Tooltip>
        )
      },
      size: 50,
    },

    // 3. Primary Contact — name + hover tooltip
    {
      id: 'primary_contact',
      accessorFn: (row) => row.primary_contact_name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Primary Contact" />
      ),
      cell: ({ row }) => {
        const name = row.original.primary_contact_name
        const email = row.original.primary_contact_email
        const phone = row.original.primary_contact_phone
        if (!name) return <span className="text-muted-foreground">—</span>

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-sm cursor-default">{name}</span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              {email && <div>{email}</div>}
              {phone && <div>{phone}</div>}
              {!email && !phone && <div>No contact info</div>}
            </TooltipContent>
          </Tooltip>
        )
      },
    },

    // 4. Aircraft Count
    {
      id: 'aircraft_count',
      accessorFn: (row) => row.aircraft_count,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Aircraft" />
      ),
      cell: ({ row }) => {
        const count = row.original.aircraft_count
        return count > 0 ? (
          <Badge variant="outline" className="text-xs font-mono">
            {count}
          </Badge>
        ) : (
          <span className="text-muted-foreground">0</span>
        )
      },
      size: 80,
    },

    // 5. CRM Stage
    {
      id: 'crm_stage',
      accessorFn: (row) => row.crm_stage,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Stage" />
      ),
      cell: ({ row }) => {
        const stage = row.original.crm_stage
        return (
          <Badge variant={CLIENT_CRM_STAGE_VARIANTS[stage]} className="text-xs">
            {CLIENT_CRM_STAGE_LABELS[stage]}
          </Badge>
        )
      },
    },

    // 6. Relationship
    {
      id: 'relationship',
      accessorFn: (row) => row.computed_relationship,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Relationship" />
      ),
      cell: ({ row }) => {
        const rel = row.original.computed_relationship
        return (
          <Badge variant={CLIENT_RELATIONSHIP_VARIANTS[rel]} className="text-xs">
            {CLIENT_RELATIONSHIP_LABELS[rel]}
          </Badge>
        )
      },
    },

    // 7. Active Contracts — count only
    {
      id: 'active_contracts',
      accessorFn: (row) => row.active_lease_count + row.active_transient_count,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Contracts" />
      ),
      cell: ({ row }) => {
        const total = row.original.active_lease_count + row.original.active_transient_count
        if (total === 0) return <span className="text-muted-foreground">—</span>
        return <span className="text-sm font-mono">{total}</span>
      },
      size: 90,
    },

    // === PORTFOLIO/ALL CONTEXTUAL ===

    {
      id: 'current_location',
      accessorFn: (row) => row.allocation_location_names.join(', '),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Location" />
      ),
      cell: ({ row }) => {
        const names = row.original.allocation_location_names
        return names.length > 0 ? (
          <span className="text-sm">{names.join(', ')}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      id: 'member_since',
      accessorFn: (row) => row.created_at,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member Since" />
      ),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.created_at).toLocaleDateString()}
        </span>
      ),
    },

    // === LEADS CONTEXTUAL (Coming Soon) ===

    {
      id: 'lead_source',
      header: 'Lead Source',
      cell: () => <ComingSoon />,
    },
    {
      id: 'last_contact',
      header: 'Last Contact',
      cell: () => <ComingSoon />,
    },
    {
      id: 'lead_score',
      header: 'Lead Score',
      cell: () => <ComingSoon />,
    },

    // === AT RISK CONTEXTUAL ===

    {
      id: 'risk_flag',
      accessorFn: (row) => row.anomalies.length,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Risk Flag" />
      ),
      cell: ({ row }) => (
        <RiskFlagCell anomalies={row.original.anomalies} row={row.original} />
      ),
    },
    {
      id: 'lease_expiry_days',
      accessorFn: (row) => {
        if (!row.nearest_lease_end_date) return Infinity
        return Math.ceil(
          (new Date(row.nearest_lease_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        )
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Lease Expiry" />
      ),
      cell: ({ row }) => (
        <LeaseExpiryCell nearestEndDate={row.original.nearest_lease_end_date} />
      ),
      size: 100,
    },
    {
      id: 'nearest_lease_end',
      accessorFn: (row) => row.nearest_lease_end_date,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="End Date" />
      ),
      cell: ({ row }) => {
        const d = row.original.nearest_lease_end_date
        return d ? (
          <span className="text-sm text-muted-foreground">
            {new Date(d).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },

    // === ACTIONS ===
    {
      id: 'actions',
      cell: ({ row }) => {
        const isDeleted = !!row.original.deleted_at
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {isDeleted ? (
                onRestore && (
                  <DropdownMenuItem onClick={() => onRestore(row.original.id)}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Restore
                  </DropdownMenuItem>
                )
              ) : (
                <>
                  <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  {onManageContacts && (
                    <DropdownMenuItem onClick={() => onManageContacts(row.original.id)}>
                      <Users className="mr-2 h-4 w-4" />
                      Contacts
                    </DropdownMenuItem>
                  )}
                  {row.original.aircraft_count > 0 && (
                    <DropdownMenuItem>
                      <Plane className="mr-2 h-4 w-4" />
                      View Aircraft
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      onDelete(row.original.id, getClientDisplayName(row.original))
                    }
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const s = filterValue.toLowerCase()
      const original = row.original
      const name = getClientDisplayName(original).toLowerCase()
      const email = (original.email_primary || '').toLowerCase()
      const phone = (original.phone_primary || '').toLowerCase()
      const clientNumber = (original.client_number || '').toLowerCase()
      const contactName = (original.primary_contact_name || '').toLowerCase()
      const tailNumbers = original.aircraft_tail_numbers.join(' ').toLowerCase()

      return (
        name.includes(s) ||
        email.includes(s) ||
        phone.includes(s) ||
        clientNumber.includes(s) ||
        contactName.includes(s) ||
        tailNumbers.includes(s)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: search,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  })

  // Group rows by selected option
  const groupedRows = useMemo(() => {
    const filteredRows = table.getRowModel().rows
    return groupRows(filteredRows, groupBy)
  }, [table.getRowModel().rows, groupBy])

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">Loading clients...</div>
                </TableCell>
              </TableRow>
            ) : groupedRows.size === 0 || (groupedRows.size === 1 && groupedRows.get('all')?.length === 0) ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">
                    No clients found. Add your first client to get started.
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              Array.from(groupedRows.entries()).map(([groupKey, rows]) => (
                <GroupSection
                  key={groupKey}
                  groupKey={groupKey}
                  groupBy={groupBy}
                  rows={rows}
                  colSpan={table.getVisibleFlatColumns().length}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination table={table} />
    </div>
  )
}

// ============================================
// Group Section Component
// ============================================

function GroupSection({
  groupKey,
  groupBy,
  rows,
  colSpan,
  onRowClick,
}: {
  groupKey: string
  groupBy: ClientGroupingOption
  rows: Row<ClientRegistryRow>[]
  colSpan: number
  onRowClick?: (id: string) => void
}) {
  if (groupBy === 'none' || groupKey === 'all') {
    return (
      <>
        {rows.map((row) => (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            className={cn(
              !!row.original.deleted_at && 'opacity-50',
              onRowClick && 'cursor-pointer hover:bg-muted/50'
            )}
            onClick={() => onRowClick?.(row.original.id)}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </>
    )
  }

  const { label, emoji } = getGroupLabel(groupKey, groupBy)

  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={colSpan} className="py-2">
          <div className="flex items-center gap-2 font-medium">
            {emoji && <span>{emoji}</span>}
            <span>{label}</span>
            <span className="text-muted-foreground font-normal">
              ({rows.length})
            </span>
          </div>
        </TableCell>
      </TableRow>
      {rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && 'selected'}
          className={cn(
            !!row.original.deleted_at && 'opacity-50',
            onRowClick && 'cursor-pointer hover:bg-muted/50'
          )}
          onClick={() => onRowClick?.(row.original.id)}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}
